import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { BACKEND_URL } from "@/lib/api";
import { ResultView, ParticipantBadge, Leaderboard } from "@/components/ResultView";
import { Podium } from "@/components/Podium";
import { SLIDE_META } from "@/lib/slides";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Lock, Unlock, Eye, EyeOff, RotateCcw, X, Loader2, Copy, Check,
  FileDown, Image as ImageIcon, Trophy, Maximize2,
} from "lucide-react";

function buildCsvRows(slide, results) {
  if (!slide) return [["No data"]];
  const t = slide.type;
  if (t === "multiple_choice" || t === "quiz") {
    const rows = [["Option", "Votes", t === "quiz" ? "Correct" : ""]];
    (results?.options || []).forEach((o) => rows.push([o.label, o.count, o.correct ? "yes" : ""]));
    return rows;
  }
  if (t === "word_cloud") {
    const rows = [["Word", "Count"]];
    (results?.words || []).forEach((w) => rows.push([w.text, w.count]));
    return rows;
  }
  if (t === "open_ended") {
    const rows = [["Response"]];
    (results?.responses || []).forEach((r) => rows.push([r.text]));
    return rows;
  }
  if (t === "scales") {
    const rows = [["Value", "Count"]];
    Object.entries(results?.distribution || {}).forEach(([k, v]) => rows.push([k, v]));
    rows.push(["Average", results?.average ?? 0]);
    return rows;
  }
  if (t === "ranking") {
    const rows = [["Rank", "Item", "Score"]];
    (results?.ranking || []).forEach((r, i) => rows.push([i + 1, r.label, r.score]));
    return rows;
  }
  if (t === "qa") {
    const rows = [["Question", "Upvotes", "Answered"]];
    (results?.questions || []).forEach((q) => rows.push([q.text, q.upvotes, q.answered ? "yes" : "no"]));
    return rows;
  }
  return [["No data"]];
}

export default function Present() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showPodium, setShowPodium] = useState(false);
  const timer = useRef(null);

  const joinUrl = `${window.location.origin}/j/${code}`;

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${code}/manage`);
      setData(res.data);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Session not found");
    }
  }, [code]);

  useEffect(() => {
    fetchState();
    timer.current = setInterval(fetchState, 2000);
    return () => clearInterval(timer.current);
  }, [fetchState]);

  const control = async (patch) => {
    try {
      const res = await api.patch(`/sessions/${code}/control`, patch);
      setData((d) => ({ ...d, session: res.data }));
      fetchState();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const resetSlide = async (slideId) => {
    if (!window.confirm("Clear all responses for this slide?")) return;
    try {
      await api.post(`/sessions/${code}/reset/${slideId}`);
      toast.success("Responses cleared");
      fetchState();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const toggleAnswered = async (qid) => {
    try {
      await api.post(`/sessions/${code}/question/${qid}/answered`);
      fetchState();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const endSession = async () => {
    if (!window.confirm("End this session? Audience will be disconnected.")) return;
    await control({ state: "closed" });
    navigate(`/summary/${code}`);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const exportCsv = () => {
    const rows = buildCsvRows(slide, slideResult);
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `slide-${idx + 1}-results.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportImage = async () => {
    const el = document.getElementById("present-slide-canvas");
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `slide-${idx + 1}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch {
      toast.error("Could not export image");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white gap-4">
        <p className="text-slate-300">{error}</p>
        <button onClick={() => navigate("/app")} className="text-indigo-400 font-semibold">← Back to dashboard</button>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;
  }

  const { session, presentation, results, participant_count } = data;
  const slides = presentation?.slides || [];
  const idx = session.active_index;
  const slide = slides[idx];
  const slideResult = results?.[idx];
  const quizDeadline = session.question_started_at
    ? new Date(session.question_started_at).getTime() + (slide?.time_limit || 20) * 1000
    : 0;
  const revealed = slide?.type === "quiz" && (session.voting_locked || (quizDeadline && Date.now() > quizDeadline));

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-indigo-600/20 blur-3xl" />

      {/* Join banner */}
      <header className="relative z-10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-2.5">
          <button onClick={() => setShowQr(true)} data-testid="present-qr-code" title="Enlarge QR"
                  className="bg-white rounded-lg p-1.5 shrink-0 hover:ring-2 hover:ring-indigo-400 transition-all relative group">
            <QRCodeSVG value={joinUrl} size={52} />
            <span className="absolute -bottom-1 -right-1 bg-indigo-600 text-white rounded-md p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-3 h-3" />
            </span>
          </button>
          <div className="flex flex-col leading-tight">
            <span className="text-slate-300 text-xs">Join at <span className="font-semibold text-white">{window.location.host}/j</span></span>
            <div className="flex items-center gap-2">
              <span className="font-mono-code font-extrabold tracking-widest text-2xl text-white" data-testid="present-join-code">{code}</span>
              <button onClick={copyLink} data-testid="copy-link-button" className="text-slate-300 hover:text-white transition-colors">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.leaderboard?.length > 0 && (
            <button onClick={() => setShowPodium(true)} data-testid="show-podium-button" title="Show podium"
                    className="inline-flex items-center gap-1.5 bg-amber-400/90 hover:bg-amber-400 text-amber-950 text-sm font-semibold rounded-xl px-3 py-2 transition-all">
              <Trophy className="w-4 h-4" /> <span className="hidden sm:inline">Podium</span>
            </button>
          )}
          {slide && (
            <>
              <button onClick={exportCsv} data-testid="export-csv-button" title="Export CSV"
                      className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-medium rounded-xl px-3 py-2 transition-all">
                <FileDown className="w-4 h-4" /> <span className="hidden sm:inline">CSV</span>
              </button>
              <button onClick={exportImage} data-testid="export-image-button" title="Export image"
                      className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-medium rounded-xl px-3 py-2 transition-all">
                <ImageIcon className="w-4 h-4" /> <span className="hidden sm:inline">Image</span>
              </button>
            </>
          )}
          <ParticipantBadge count={participant_count} />
          <button onClick={endSession} data-testid="end-session-button"
                  className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-red-500/80 border border-white/15 text-white text-sm font-medium rounded-xl px-3 py-2 transition-all">
            <X className="w-4 h-4" /> End
          </button>
          {data.logo_url && (
            <img src={`${BACKEND_URL}${data.logo_url}`} alt="logo" className="h-12 w-auto max-w-[220px] object-contain ml-1" />
          )}
        </div>
      </header>

      {/* Slide canvas */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-8 pb-28">
        <div id="present-slide-canvas" className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl p-8 sm:p-12 min-h-[60vh] flex flex-col">
          {idx === -1 ? (
            <WelcomeSlide joinUrl={joinUrl} code={code} host={window.location.host} count={participant_count}
                          logo={data.logo_url ? `${BACKEND_URL}${data.logo_url}` : null} />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold uppercase tracking-widest text-indigo-600">
                  {slide ? SLIDE_META[slide.type].label : ""} · Slide {idx + 2}/{slides.length + 1}
                </span>
                {session.voting_locked && <span className="text-xs font-semibold text-amber-600 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Voting locked</span>}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-8">
                {slide?.question}
              </h1>
              <div className="flex-1 flex flex-col justify-center">
                {session.show_results ? (
                  <ResultView slide={slide} results={slideResult} revealed={revealed} onToggleAnswered={slide?.type === "qa" ? toggleAnswered : undefined} />
                ) : (
                  <div className="text-center text-slate-400 py-16 text-xl">
                    Results hidden · <span className="font-semibold text-slate-500">{slideResult?.total || 0}</span> responses so far
                  </div>
                )}
              </div>
              {slide?.type === "quiz" && session.show_results && (
                <div className="mt-8 pt-6 border-t border-slate-100 grid md:grid-cols-2 gap-8">
                  <Leaderboard entries={slideResult?.leaderboard} title="This question" />
                  <Leaderboard entries={data.leaderboard} title="Overall leaderboard" />
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Control bar */}
      <div className="fixed bottom-0 inset-x-0 z-20 p-4 flex justify-center">
        <div className="flex items-center gap-2 bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl px-3 py-2 shadow-2xl">
          <CtrlBtn testid="prev-slide-button" disabled={idx <= -1} onClick={() => control({ active_index: idx - 1 })} icon={ChevronLeft} />
          <span className="text-white text-sm font-mono-code px-2 tabular-nums">{idx + 2} / {slides.length + 1}</span>
          <CtrlBtn testid="next-slide-button" disabled={idx >= slides.length - 1} onClick={() => control({ active_index: idx + 1 })} icon={ChevronRight} />
          <div className="w-px h-6 bg-white/10 mx-1" />
          <CtrlBtn testid="toggle-results-button" active={!session.show_results}
                   onClick={() => control({ show_results: !session.show_results })}
                   icon={session.show_results ? Eye : EyeOff} label={session.show_results ? "Hide" : "Show"} />
          <CtrlBtn testid="toggle-lock-button" active={session.voting_locked}
                   onClick={() => control({ voting_locked: !session.voting_locked })}
                   icon={session.voting_locked ? Lock : Unlock} label={session.voting_locked ? "Locked" : "Lock"} />
          <CtrlBtn testid="reset-slide-button" disabled={!slide} onClick={() => slide && resetSlide(slide.id)} icon={RotateCcw} label="Reset" />
        </div>
      </div>

      {showQr && <QrModal joinUrl={joinUrl} code={code} host={window.location.host} onClose={() => setShowQr(false)} />}
      {showPodium && <Podium entries={data.leaderboard} onClose={() => setShowPodium(false)} />}
    </div>
  );
}

function WelcomeSlide({ joinUrl, code, host, count, logo }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-5" data-testid="welcome-slide">
      {logo && <img src={logo} alt="logo" className="h-12 w-auto max-w-[220px] object-contain" />}
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Join now</h1>
      <div className="bg-white rounded-3xl border-2 border-slate-200 p-5 shadow-sm">
        <QRCodeSVG value={joinUrl} size={220} />
      </div>
      <div>
        <p className="text-slate-500 text-lg">Go to <span className="font-semibold text-slate-900">{host}/j</span></p>
        <p className="font-mono-code font-extrabold tracking-[0.25em] text-4xl sm:text-5xl text-indigo-600 mt-2">{code}</p>
      </div>
      <ParticipantBadge count={count} />
      <p className="text-slate-400 text-sm">Your first question will appear when everyone is in.</p>
    </div>
  );
}

function QrModal({ joinUrl, code, host, onClose }) {  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex flex-col items-center justify-center p-6" data-testid="qr-modal" onClick={onClose}>
      <button onClick={onClose} data-testid="qr-modal-close" className="absolute top-6 right-6 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all">
        <X className="w-6 h-6" />
      </button>
      <div className="bg-white rounded-3xl p-8 shadow-2xl animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <QRCodeSVG value={joinUrl} size={Math.min(window.innerWidth - 120, 460)} />
      </div>
      <div className="mt-8 text-center">
        <p className="text-slate-300 text-lg">Go to <span className="font-semibold text-white">{host}/j</span></p>
        <p className="font-mono-code font-extrabold tracking-[0.3em] text-5xl sm:text-7xl text-white mt-2">{code}</p>
      </div>
    </div>
  );
}

function CtrlBtn({ icon: Icon, label, onClick, disabled, active, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-all disabled:opacity-30 ${active ? "bg-indigo-500 text-white" : "text-slate-200 hover:bg-white/10"}`}
    >
      <Icon className="w-5 h-5" />
      {label && <span className="hidden sm:inline">{label}</span>}
    </button>
  );
}
